import { DownloadIcon } from "@chakra-ui/icons";
import {
  Accordion,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Spinner,
  Tag,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ResponseOf } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { VisitInstanceDailyNoteId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import CollapsibleSection from "./CollapsibleSection";
import { matchRulesToAnswers, triggeringAnswerIndexes } from "./triggeringAnswers";
import { useDailyNoteDocumentDownload } from "./useDailyNoteDocumentDownload";

// Derived from the endpoint rather than looked up in `Messages`: this response is
// inlined into the operation rather than emitted as a named schema component.
type NoteDetails = ResponseOf<"get", "./visit_daily_notes/:visitInstanceDailyNoteId">;
type Answer = NoteDetails["answers"][number];

/**
 * One daily note in full: every question as the caregiver was asked it, and every
 * answer as they gave it.
 *
 * The questions come from the note itself rather than from a catalog — the
 * questionnaire is versioned on FreedomCare's side and we hold no copy of it — so
 * a note written against an older version still reads here the way it was asked.
 */
/** The single line of text a section header usually is. */
function priorityScheme(priority: NoteDetails["priority"]): string {
  switch (priority) {
    case "URGENT":
      return "red";
    case "HIGH":
      return "orange";
    default:
      return "green";
  }
}

function SectionTitle(props: { children: React.ReactNode }) {
  return (
    <Text fontSize="md" fontWeight="semibold">
      {props.children}
    </Text>
  );
}

export default function DailyNoteDetailsDrawer(props: {
  visitInstanceDailyNoteId: VisitInstanceDailyNoteId | null;
  onClose: () => void;
}) {
  const { queries } = useApi();

  const isOpen = props.visitInstanceDailyNoteId !== null;

  // `enabled` is what makes the non-null assertion safe: the query never runs
  // while the drawer is closed and no id has been picked.
  const query = useQuery({
    ...queries.dailyNotes.byId(props.visitInstanceDailyNoteId!),
    enabled: isOpen,
  });

  const note = query.data ?? null;

  // Which answers each triggered rule was about, worked out once and used twice: under the
  // rule, to show what the caregiver wrote, and in the answer list, to mark where it came from.
  const ruleMatches = note === null ? [] : matchRulesToAnswers(note.triggeredRules, note.answers);
  const triggering = triggeringAnswerIndexes(ruleMatches);

  const pdf = useDailyNoteDocumentDownload();

  return (
    <Drawer
      // This drawer also opens from inside the attestation modal, and a Modal locks
      // body scroll while it is mounted. Left on, that lock reaches the drawer too and
      // a long note becomes unreadable with no way to reach the bottom.
      blockScrollOnMount={false}
      isOpen={isOpen}
      size="xl"
      onClose={props.onClose}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pr={20}>
          <Flex align="center" gap={3} justify="space-between">
            <Text>Daily note</Text>
            {/* Offered for any note, signed or not: the document is the caregiver's
                report of the visit, which exists from the moment the note arrives and
                does not wait on a nurse. */}
            {note === null ? null : (
              <Tooltip label="Download this daily note as a PDF">
                <Button
                  isLoading={pdf.isPending(note.id)}
                  leftIcon={<DownloadIcon />}
                  size="sm"
                  variant="outline"
                  onClick={() => void pdf.downloadOne(note.id)}
                >
                  Download
                </Button>
              </Tooltip>
            )}
          </Flex>
        </DrawerHeader>

        <DrawerBody overflowY="auto" pb={8}>
          {note === null ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : (
            <Flex direction="column" gap={5}>
              <Box>
                <Heading color="gray.900" size="lg">
                  {note.patientName}
                </Heading>

                {/* Labelled rather than run together after a middle dot: a date and a
                    name side by side give no clue which is the caregiver, and the
                    caregiver is who a reviewer follows up with. */}
                <Flex gap={8} mt={3} wrap="wrap">
                  <Box>
                    <FieldLabel mb={0}>Visit</FieldLabel>
                    <Text color="gray.900" fontSize="md" fontWeight="medium">
                      {dateFormatter.toDateTime(note.visitStartTime)}
                    </Text>
                  </Box>

                  {note.caregiverName === null ? null : (
                    <Box>
                      <FieldLabel mb={0}>Caregiver</FieldLabel>
                      <Text color="gray.900" fontSize="md" fontWeight="medium">
                        {note.caregiverName}
                      </Text>
                    </Box>
                  )}
                </Flex>
              </Box>

              <Flex gap={2} wrap="wrap">
                {note.priority === null ? null : (
                  <Tag colorScheme={priorityScheme(note.priority)}>{note.priority}</Tag>
                )}
                {note.reviewPathways.map((pathway) => (
                  <Tag key={pathway} colorScheme="blue">
                    {pathway === "RN" ? "RN" : "Case management"}
                  </Tag>
                ))}
                {note.determination === null ? null : (
                  <Tag colorScheme={note.determination === "NON_ROUTINE" ? "red" : "blue"}>
                    Nurse: {note.determination === "NON_ROUTINE" ? "concerning" : "not confirmed"}
                  </Tag>
                )}
                {/* Provenance rather than meaning -- outlined so it stays legible
                    without competing with the priority and status beside it. */}
                <Tag colorScheme="gray" variant="outline">
                  Revision {note.revision}
                </Tag>
                {note.questionnaireVersion === null ? null : (
                  <Tag colorScheme="gray" variant="outline">
                    Questionnaire {note.questionnaireVersion}
                  </Tag>
                )}
                {note.locale === null ? null : (
                  <Tag colorScheme="gray" variant="outline">
                    {note.locale}
                  </Tag>
                )}
                <Tag
                  colorScheme={
                    note.status === "APPROVED"
                      ? "green"
                      : note.status === "AWAITING_COSIGNATURE"
                      ? "purple"
                      : "gray"
                  }
                >
                  {note.status === "APPROVED"
                    ? "Signed"
                    : note.status === "AWAITING_COSIGNATURE"
                    ? "Awaiting co-signature"
                    : "Ready for review"}
                </Tag>
              </Flex>

              <Accordion
                // Keyed on the note so opening another one starts from its own defaults.
                key={note.id}
                allowMultiple={true}
                // What the caregiver wrote is why anyone opens this drawer, and the
                // classification explains the badges above it -- both start open. The
                // signed declaration is a long block of fixed legal text that is
                // identical on every note in a batch, so it starts closed.
                //
                // The exception is a note the classifier called HIGH or URGENT: its
                // Classification section already quotes the answers that raised the flag,
                // with the caregiver's comment, so the full list starts closed and the
                // nurse is not left scrolling past it to reach the part that matters.
                defaultIndex={openSections(note)}
                reduceMotion={true}
              >
                <CollapsibleSection header={<SectionTitle>Classification</SectionTitle>}>
                  <AnalysisSection note={note} ruleMatches={ruleMatches} />
                </CollapsibleSection>

                {note.attestation === null ? null : (
                  <CollapsibleSection
                    header={
                      <SectionTitle>
                        Signed by {note.attestation.signedByName} on{" "}
                        {dateFormatter.toDateTime(note.attestation.signedAt)}
                      </SectionTitle>
                    }
                  >
                    <Text color="gray.700" fontSize="md" whiteSpace="pre-wrap">
                      {note.attestation.declarationText}
                    </Text>
                    {note.attestation.requiresCosignature ? (
                      <Text fontSize="md" fontWeight="semibold" mt={3}>
                        {note.attestation.cosignedAt === null
                          ? "Awaiting a 2nd reviewer"
                          : `Co-signed by ${note.attestation.cosignedByName ?? ""} on ` +
                            `${dateFormatter.toDateTime(note.attestation.cosignedAt)}`}
                      </Text>
                    ) : null}
                  </CollapsibleSection>
                )}

                <CollapsibleSection
                  header={<SectionTitle>Caregiver answers ({note.answers.length})</SectionTitle>}
                >
                  <Flex direction="column" gap={4}>
                    {note.answers.map((answer, index) => (
                      <AnswerRow
                        key={`${answer.questionId}-${index}`}
                        answer={answer}
                        isTriggering={triggering.has(index)}
                      />
                    ))}
                  </Flex>
                </CollapsibleSection>
              </Accordion>
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Which accordion sections start open: Classification first, the signed declaration only when
 * there is one (and then closed), and the full caregiver answers unless the note is concerning.
 */
function openSections(note: NoteDetails): number[] {
  const isNonRoutine = note.priority === "HIGH" || note.priority === "URGENT";
  const answersIndex = note.attestation === null ? 1 : 2;

  return isNonRoutine ? [0] : [0, answersIndex];
}

function AnalysisSection(props: { note: NoteDetails; ruleMatches: number[][] }) {
  const { note } = props;

  if (note.analysisState === "FAILED") {
    return (
      <Box bg="orange.50" borderRadius="md" p={3}>
        <Text fontSize="md">
          No summary could be produced for this note. Read the answers below.
        </Text>
      </Box>
    );
  }

  if (note.summary === null) {
    // A note triaged as routine is complete without a summary: the full model never reads
    // it. Only a note still in the queue is "being written".
    return (
      <Box bg="gray.50" borderRadius="md" p={3}>
        <Text color="gray.600" fontSize="md">
          {note.analysisState === "COMPLETED"
            ? "This note was triaged as non-concerning, so no summary was written. Read the answers below."
            : "Summary is still being written."}
        </Text>
      </Box>
    );
  }

  // The card takes the priority's own colour rather than a two-way concerning/not
  // split, so it always agrees with the tag above it: HIGH was orange there and red
  // here.
  const scheme = priorityScheme(note.priority);

  return (
    <Flex direction="column" gap={6}>
      {/* What the note says, and why it was picked out. The two belong together --
          the second is a claim about the first -- so they share one card, tinted by
          how serious the classifier thought it was. */}
      <Box
        bg={`${scheme}.50`}
        borderColor={`${scheme}.200`}
        borderRadius="md"
        borderWidth={1}
        p={4}
      >
        <Flex align="flex-start" gap={4} justify="space-between">
          <Text fontSize="md">{note.summary}</Text>
          {note.confidence === null ? null : <ConfidenceMeter confidence={note.confidence} />}
        </Flex>
        {note.primaryReason === null ? null : (
          <Text color={`${scheme}.800`} fontSize="md" fontWeight="semibold" mt={3}>
            {note.primaryReason}
          </Text>
        )}
        {note.suggestedIncidentSubtype === null ? null : (
          <Text color="gray.600" fontSize="sm" mt={1}>
            Suggested incident subtype: {note.suggestedIncidentSubtype}
          </Text>
        )}
      </Box>

      {/* The audit trail. A nurse cannot tell a real finding from a false positive
          without seeing which rule fired and on what words, and it is the only
          material there is for tuning the prompt later. */}
      {note.triggeredRules.length === 0 ? null : (
        <Box>
          <FieldLabel>Triggered rules</FieldLabel>
          <Flex direction="column" gap={3}>
            {note.triggeredRules.map((rule, index) => (
              <Box
                key={`${rule.rule}-${index}`}
                bg="gray.50"
                borderLeftColor="gray.400"
                borderLeftWidth={3}
                borderRadius="sm"
                px={3}
                py={2}
              >
                <Text color="gray.900" fontSize="md" fontWeight="semibold">
                  {rule.rule}
                </Text>
                <Text color="gray.600" fontSize="md" fontStyle="italic" mt={1}>
                  “{rule.evidence}”
                </Text>
                {/* What the caregiver actually wrote where this came from, the open comment
                    included: the model's quote alone does not show the nurse the context. */}
                {(props.ruleMatches[index] ?? []).map((answerIndex) => {
                  const source = note.answers[answerIndex];

                  return source === undefined ? null : (
                    <Box
                      key={answerIndex}
                      bg="red.50"
                      borderColor="red.200"
                      borderRadius="sm"
                      borderWidth={1}
                      mt={2}
                      px={3}
                      py={2}
                    >
                      <Text color="gray.600" fontSize="sm">
                        {source.question}
                      </Text>
                      {source.answer.length === 0 ? null : (
                        <Text color="gray.900" fontSize="md" fontWeight="semibold">
                          {source.answer.join("; ")}
                        </Text>
                      )}
                      {source.comment === null || source.comment === "" ? null : (
                        <Text color="gray.900" fontSize="md" fontWeight="medium" mt={1}>
                          “{source.comment}”
                        </Text>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {note.clinicalSignals.length === 0 ? null : (
        <Box>
          <FieldLabel>Clinical signals</FieldLabel>
          <Flex gap={2} wrap="wrap">
            {note.clinicalSignals.map((signal) => (
              <Tag key={signal} colorScheme="gray" size="md" variant="subtle">
                {signal}
              </Tag>
            ))}
          </Flex>
        </Box>
      )}

      {/* Two readings of the same classification -- how it read the words, and what it
          suggests doing -- so they sit side by side rather than stacked as two more
          anonymous blocks. */}
      {note.contextInterpretation === null && note.recommendedReview === null ? null : (
        <Flex direction={{ base: "column", md: "row" }} gap={5}>
          {note.contextInterpretation === null ? null : (
            <Box flex="1">
              <FieldLabel>How it read the note</FieldLabel>
              <Text fontSize="md">{note.contextInterpretation}</Text>
            </Box>
          )}

          {note.recommendedReview === null ? null : (
            <Box flex="1">
              <FieldLabel>Recommended review</FieldLabel>
              <Text fontSize="md">{note.recommendedReview}</Text>
            </Box>
          )}
        </Flex>
      )}
    </Flex>
  );
}

/**
 * How sure the classifier was, as three bars rather than a word.
 *
 * It sits beside the summary because it qualifies that claim, not the section: as a
 * line at the bottom it read as one more finding, when it is really a caveat on
 * everything above it. Bars because the scale is ordered -- a reader can see "less
 * than full" without reading anything.
 *
 * Coloured only when LOW. HIGH is the expected case, and spending a colour on it
 * would say "nothing is wrong" in the loudest available voice.
 */
function ConfidenceMeter(props: { confidence: NonNullable<NoteDetails["confidence"]> }) {
  const filled = props.confidence === "HIGH" ? 3 : props.confidence === "MEDIUM" ? 2 : 1;
  const scheme = props.confidence === "LOW" ? "orange" : "gray";

  return (
    <Tooltip label={`Classifier confidence: ${props.confidence.toLowerCase()}`}>
      <Flex align="flex-end" flexShrink={0} gap="2px" h="14px" title="">
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            bg={index < filled ? `${scheme}.500` : `${scheme}.200`}
            borderRadius="sm"
            h={`${6 + index * 4}px`}
            w="4px"
          />
        ))}
      </Flex>
    </Tooltip>
  );
}

/** The one label style every field in this drawer uses. */
function FieldLabel(props: { children: React.ReactNode; mb?: number }) {
  return (
    <Text
      color="blue.700"
      fontSize="xs"
      fontWeight="semibold"
      letterSpacing="wide"
      mb={props.mb ?? 2}
      textTransform="uppercase"
    >
      {props.children}
    </Text>
  );
}

function AnswerRow(props: { answer: Answer; isTriggering: boolean }) {
  const { answer, isTriggering } = props;

  const category = [answer.category, answer.subCategory].filter((x) => x !== null).join(" / ");

  const answered = answer.answer.length > 0;

  return (
    // A light red band behind the answers the classifier's rules were about, so the nurse
    // can see where a flag came from without reading every answer.
    <Box
      bg={isTriggering ? "red.50" : undefined}
      borderColor={isTriggering ? "red.300" : "gray.200"}
      borderLeftWidth={3}
      borderRadius={isTriggering ? "sm" : undefined}
      pl={3}
      pr={isTriggering ? 3 : undefined}
      py={isTriggering ? 2 : 1}
    >
      {category === "" ? null : <FieldLabel mb={1}>{category}</FieldLabel>}
      <Text color="gray.700" fontSize="md">
        {answer.question}
      </Text>
      {/* The answer carries the weight: it is what someone scanning the note is
          looking for, and an unanswered question should look unanswered rather than
          like an em dash someone has to decode. */}
      <Text
        color={answered ? "gray.900" : "gray.400"}
        fontSize="md"
        fontWeight={answered ? "semibold" : "normal"}
        mt={1}
      >
        {answered ? answer.answer.join("; ") : "No answer"}
      </Text>
      {answer.comment === null || answer.comment === "" ? null : (
        <Text color="gray.600" fontSize="md" fontStyle="italic" mt={2}>
          “{answer.comment}”
        </Text>
      )}
    </Box>
  );
}
